import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, CheckCircle2, X, Phone, Shield, Zap, MapPin } from "lucide-react";

const usePageMeta = (title, description, canonical) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc && description) desc.setAttribute("content", description);

    let canon = document.querySelector('link[rel="canonical"]');
    const prevCanon = canon ? canon.getAttribute("href") : null;
    if (canonical) {
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", canonical);
    }
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
      if (canon && prevCanon != null) canon.setAttribute("href", prevCanon);
    };
  }, [title, description, canonical]);
};

// ============================================================================
// COMPETITOR / ALTERNATIVE SEO PAGES.
//
// Comparative-advertising SEO targeting high-intent queries like:
//   "PrisonConnect alternative", "Cellblock B vs PrisonConnect",
//   "PrisonEngine alternative", "cheapest prison call service Australia".
//
// All claims are factual and based on publicly-listed pricing on each
// competitor's homepage at time of writing. Australian Consumer Law permits
// comparative advertising provided claims are truthful and not misleading.
// ============================================================================

const Hdr = () => (
  <header className="border-b" style={{ borderColor: "var(--line)", background: "rgba(8,8,10,0.85)", backdropFilter: "blur(8px)" }}>
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5" data-testid="competitor-back-home">
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--accent)", color: "#0b0b0c" }}>
          <Lock size={18} strokeWidth={2.5} />
        </div>
        <div className="font-display text-xl">CELLBLOCK B</div>
      </Link>
      <div className="flex items-center gap-5 text-sm">
        <Link to="/" style={{ color: "var(--muted)" }} className="hover:text-white transition hidden sm:inline">Home</Link>
        <Link to="/#pricing" style={{ color: "var(--muted)" }} className="hover:text-white transition hidden sm:inline">Pricing</Link>
        <Link
          to="/#signup"
          className="btn-primary"
          data-testid="competitor-get-number-cta"
          onClick={() => window.cbbTrack && window.cbbTrack("begin_signup", { source: "competitor_header" })}
        >
          Get My Number <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  </header>
);

const Ftr = () => (
  <footer className="border-t mt-20" style={{ borderColor: "var(--line)", background: "var(--bg-2)" }}>
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-8 text-sm" style={{ color: "var(--muted)" }}>
      <div>
        <div className="font-display text-xl text-white">CELLBLOCK B</div>
        <p className="mt-2">Australia's cheapest prison call numbers. $1 a day.</p>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] mb-3">Compare</div>
        <ul className="space-y-1.5">
          <li><Link to="/cellblock-b-vs-prisonconnect" className="hover:text-white transition">Cellblock B vs PrisonConnect</Link></li>
          <li><Link to="/cellblock-b-vs-prisonengine" className="hover:text-white transition">Cellblock B vs PrisonEngine</Link></li>
          <li><Link to="/prisonconnect-alternative" className="hover:text-white transition">PrisonConnect alternative</Link></li>
          <li><Link to="/prisonengine-alternative" className="hover:text-white transition">PrisonEngine alternative</Link></li>
          <li><Link to="/cheapest-prison-call-service-australia" className="hover:text-white transition">Cheapest in Australia</Link></li>
        </ul>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] mb-3">Site</div>
        <ul className="space-y-1.5">
          <li><Link to="/" className="hover:text-white transition">Home</Link></li>
          <li><Link to="/#pricing" className="hover:text-white transition">Pricing</Link></li>
          <li><Link to="/privacy-policy" className="hover:text-white transition">Privacy Policy</Link></li>
          <li><Link to="/refund-policy" className="hover:text-white transition">Refund Policy</Link></li>
          <li><Link to="/terms-and-conditions" className="hover:text-white transition">Terms &amp; Conditions</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t" style={{ borderColor: "var(--line)" }}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 text-xs flex flex-wrap items-center justify-between gap-3" style={{ color: "var(--muted)" }}>
        <div>© {new Date().getFullYear()} Cellblock B. All Rights Reserved.</div>
        <div className="font-mono">cellblockb.com.au</div>
      </div>
    </div>
  </footer>
);

const Disclaimer = ({ brand }) => (
  <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 text-xs leading-relaxed" style={{ color: "var(--muted)" }}>
    Cellblock B is independent and is not endorsed by, approved by, or affiliated with {brand} or any
    other prison phone provider, correctional centre, or government department. {brand} is a
    trademark of its respective owner. Comparisons reference publicly-listed pricing and feature
    information at the time of writing and may change. Always verify current pricing on each
    provider's own website before making a decision.
  </div>
);

// ----------------------------------------------------------------------------
// Comparison table component
// ----------------------------------------------------------------------------
const CompareTable = ({ rows }) => (
  <div className="card overflow-hidden">
    <div className="grid grid-cols-[1.4fr_1fr_1fr] text-xs font-mono uppercase tracking-[0.16em]">
      <div className="px-4 py-3 border-b" style={{ borderColor: "var(--line)", color: "var(--muted)" }}>Feature</div>
      <div className="px-4 py-3 border-b text-center" style={{ borderColor: "var(--line)", color: "var(--accent)", background: "rgba(255,193,7,0.06)" }}>Cellblock B</div>
      <div className="px-4 py-3 border-b text-center" style={{ borderColor: "var(--line)", color: "var(--muted)" }}>{rows.competitorName}</div>
    </div>
    {rows.items.map((row, i) => (
      <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr] text-sm border-b" style={{ borderColor: "var(--line)" }}>
        <div className="px-4 py-3.5" style={{ color: "rgba(255,255,255,0.9)" }}>{row.label}</div>
        <div className="px-4 py-3.5 text-center" style={{ background: "rgba(255,193,7,0.04)" }}>
          {row.cbb === true ? <CheckCircle2 size={16} className="inline" style={{ color: "var(--accent)" }} /> : row.cbb === false ? <X size={16} className="inline" style={{ color: "#666" }} /> : <span style={{ color: "rgba(255,255,255,0.9)" }}>{row.cbb}</span>}
        </div>
        <div className="px-4 py-3.5 text-center" style={{ color: "var(--muted)" }}>
          {row.them === true ? <CheckCircle2 size={16} className="inline" style={{ color: "#7f7f7f" }} /> : row.them === false ? <X size={16} className="inline" style={{ color: "#666" }} /> : row.them}
        </div>
      </div>
    ))}
  </div>
);

// ----------------------------------------------------------------------------
// Page-shell wrapper shared by every competitor page
// ----------------------------------------------------------------------------
const Shell = ({ slug, title, description, h1, intro, brand, table, faq, bottomBlurb }) => {
  usePageMeta(title, description, `https://cellblockb.com.au/${slug}`);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <div className="App relative min-h-screen" data-testid={`competitor-page-${slug}`}>
      <Hdr />
      <script
        type="application/ld+json"
        // Static, hardcoded Schema.org JSON-LD. No user input — safe.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="py-16 sm:py-24 relative">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>
            <Shield size={12} />
            <span>COMPARE · AUSTRALIA-WIDE</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">{h1}</h1>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
            {intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/#signup"
              className="btn-primary"
              data-testid={`competitor-hero-cta-${slug}`}
              onClick={() => window.cbbTrack && window.cbbTrack("begin_signup", { source: `competitor_hero_${slug}` })}
            >
              Get My Cellblock B Number <ArrowRight size={16} />
            </Link>
            <Link to="/#pricing" className="btn-ghost">See Pricing</Link>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-12 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Side-by-side</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">Cellblock B vs {brand} — Side By Side</h2>
          <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>
            Based on each provider's publicly-listed plans. Verify current pricing on each provider's
            website before making a decision.
          </p>
          <div className="mt-7">
            <CompareTable rows={table} />
          </div>
          <p className="mt-4 text-xs leading-relaxed" style={{ color: "var(--muted)" }}>
            {bottomBlurb}
          </p>
        </div>
      </section>

      {/* Why switch */}
      <section className="py-12 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Why switch</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">Three reasons families switch to Cellblock B</h2>
          <div className="grid sm:grid-cols-3 gap-5 mt-7">
            {[
              { icon: Zap, title: "FLAT — UNLIMITED CALLS", body: "$1 a day ($30 every 30 days) covers as many prison calls as you take. No per-minute add-ons, no surprise top-ups." },
              { icon: MapPin, title: "EVERY AUSTRALIAN PRISON", body: "Local landlines for every facility, every state, every territory — from Risdon to Casuarina to Darwin." },
              { icon: Phone, title: "NO APP. NO NEW SIM.", body: "Calls land on the mobile you already carry. Activation takes minutes — not a postal verification cycle." },
            ].map(({ icon: I, title, body }) => (
              <div key={title} className="card p-5">
                <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(255,193,7,0.10)", color: "var(--accent)" }}>
                  <I size={18} />
                </div>
                <div className="font-display text-base mt-3 tracking-wide">{title}</div>
                <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Cellblock B pricing</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">Lowest price in Australia — guaranteed.</h2>
          <div className="mt-7 max-w-xl mx-auto card p-7" style={{ borderColor: "var(--accent)", borderWidth: 1, borderStyle: "solid" }}>
            <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>Cheapest in Australia</div>
            <div className="flex items-baseline gap-2 mt-2">
              <div className="font-display text-7xl" style={{ color: "var(--accent)" }}>$1</div>
              <div className="text-sm" style={{ color: "var(--muted)" }}>/ day</div>
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
              Billed <span className="text-white font-semibold">$30 every 30 days</span>. Cancel anytime — no contracts, no fees.
            </div>
            <Link
              to="/#signup"
              className="btn-primary mt-6 w-full justify-center"
              onClick={() => {
                window.cbbTrack && window.cbbTrack("select_plan", { plan_name: "one_dollar_day", value: 30, currency: "AUD", source: `competitor_${slug}` });
              }}
            >
              Start — $1 a day <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>FAQ</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">Common Questions</h2>
          <div className="mt-6 space-y-4">
            {faq.map(([q, a]) => (
              <div key={q} className="card p-5">
                <div className="font-semibold text-white">{q}</div>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 border-t" style={{ borderColor: "var(--line)", background: "var(--bg-2)" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl">Switch to Cellblock B today.</h2>
          <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>
            $1 a day. Billed $30 every 30 days. Unlimited calls. Cancel anytime.
          </p>
          <Link
            to="/#signup"
            className="btn-primary mt-7 inline-flex"
            data-testid={`competitor-final-cta-${slug}`}
            onClick={() => window.cbbTrack && window.cbbTrack("begin_signup", { source: `competitor_footer_${slug}` })}
          >
            Get My Number <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Disclaimer brand={brand} />
      <Ftr />
    </div>
  );
};

// ============================================================================
// Page 1 — PrisonConnect alternative
// ============================================================================
export const PrisonConnectAlternativePage = () => (
  <Shell
    slug="prisonconnect-alternative"
    title="PrisonConnect Alternative | Cellblock B — Cheaper, Flat-Rate Prison Calls"
    description="Looking for a cheaper PrisonConnect alternative? Cellblock B is $1 a day ($30 every 30 days) — flat-rate, unlimited prison calls forwarded to your mobile. Cancel anytime."
    h1="A cheaper PrisonConnect alternative."
    intro="If you're paying weekly fees plus per-minute call charges for a PrisonConnect local-landline forwarding service, you can almost certainly cut your bill with Cellblock B. We charge one flat subscription — $1 a day — billed $30 every 30 days — and you get unlimited calls forwarded straight to your mobile from any Australian correctional centre."
    brand="PrisonConnect"
    bottomBlurb="Pricing references are taken from PrisonConnect's publicly-listed plans on prisonconnect.com.au at time of writing. Cellblock B is independent and not affiliated with PrisonConnect. Plans and pricing on either side may change."
    table={{
      competitorName: "PrisonConnect",
      items: [
        { label: "Headline price", cbb: "$1 a day ($30 every 30 days)", them: "From $4.60/week + call usage" },
        { label: "Unlimited calls included", cbb: true, them: false },
        { label: "Cancel anytime", cbb: true, them: true },
        { label: "Lowest-price guarantee", cbb: true, them: false },
        { label: "Local Australian landline number", cbb: true, them: true },
        { label: "Forwards to your existing mobile", cbb: true, them: true },
        { label: "No app required", cbb: true, them: true },
        { label: "Instant activation", cbb: true, them: "Varies" },
        { label: "Cancel anytime", cbb: true, them: true },
        { label: "10-day money-back guarantee", cbb: true, them: false },
        { label: "Auto-renews until cancelled", cbb: true, them: true },
      ],
    }}
    faq={[
      ["Is Cellblock B actually cheaper than PrisonConnect?", "On a like-for-like mobile-forwarding plan, yes — Cellblock B is one flat $1 a day ($30 every 30 days) with unlimited calls. PrisonConnect's mobile plan is priced weekly and varies with usage. Always check both providers' current published rates before deciding."],
      ["Do I need a new SIM or app to switch?", "No. Cellblock B works with the mobile and SIM you already own. We just give you a new local landline number that the inmate dials — we then forward the call to your mobile automatically."],
      ["How fast can I switch from PrisonConnect?", "Numbers are provisioned automatically as soon as your payment is confirmed. Most customers receive their new local landline number by email within a few minutes of subscribing."],
      ["What if my loved one is transferred to a different prison?", "Cellblock B will provide a replacement local number in the new state where available, at no additional cost."],
      ["Can I cancel anytime?", "Yes. Cancel any time from your billing portal — subscriptions will not auto-renew after cancellation. We also offer a 10-day money-back guarantee."],
    ]}
  />
);

// ============================================================================
// Page 2 — Cellblock B vs PrisonConnect (head-to-head)
// ============================================================================
export const CellblockBVsPrisonConnectPage = () => (
  <Shell
    slug="cellblock-b-vs-prisonconnect"
    title="Cellblock B vs PrisonConnect | Prison Call Price & Feature Comparison"
    description="Cellblock B vs PrisonConnect — full side-by-side comparison of prison call forwarding plans in Australia. Pricing, features, coverage and FAQs. Cellblock B from $1 a day — billed $30 every 30 days."
    h1="Cellblock B vs PrisonConnect."
    intro="Both services give you a local Australian landline that an inmate can dial as a normal local call — then bridge it to your mobile. Where they differ is pricing model, included call volume, and guarantee. Here's the head-to-head, based on each provider's publicly-listed plans."
    brand="PrisonConnect"
    bottomBlurb="All comparisons use publicly-listed plans on each provider's website at time of writing. Pricing on either side may change — verify current rates on the relevant homepage before deciding."
    table={{
      competitorName: "PrisonConnect",
      items: [
        
        { label: "Headline price", cbb: "$1 a day ($30 / 30 days)", them: "~$19.94 (from $4.60/wk)" },
        
        { label: "Unlimited calls included", cbb: true, them: false },
        { label: "Per-minute call charges", cbb: false, them: true },
        { label: "Lowest-price guarantee", cbb: true, them: false },
        { label: "10-day money-back guarantee", cbb: true, them: false },
        { label: "Local landline + mobile forwarding", cbb: true, them: true },
        { label: "All Australian states & territories", cbb: true, them: true },
        { label: "Cancel anytime", cbb: true, them: true },
      ],
    }}
    faq={[
      ["What's the biggest practical difference?", "Cellblock B includes unlimited calls in the flat subscription. PrisonConnect's plans typically include the local number and forward calls, but call usage is charged separately. For high-call-volume families, Cellblock B is materially cheaper in practice."],
      ["Is PrisonConnect a scam?", "No. PrisonConnect is a legitimate Australian prison-call forwarding provider. Cellblock B simply prices differently — flat unlimited instead of per-usage — and offers a 10-day money-back guarantee."],
      ["Will my loved one notice the switch?", "Only the number they dial changes. The experience inside the prison is identical — they dial a local landline and you answer on your mobile."],
      ["Can I keep my PrisonConnect number?", "Numbers are tied to each provider's telephony platform and aren't portable between services. Cellblock B will issue you a fresh local landline number in the relevant state on signup."],
    ]}
  />
);

// ============================================================================
// Page 3 — PrisonEngine alternative
// ============================================================================
export const PrisonEngineAlternativePage = () => (
  <Shell
    slug="prisonengine-alternative"
    title="PrisonEngine Alternative | Cellblock B — Lowest-Price Prison Calls"
    description="Cellblock B is the cheapest PrisonEngine alternative in Australia — $1 a day ($30 every 30 days) for unlimited prison calls forwarded to your mobile. Cancel anytime."
    h1="A cheaper PrisonEngine alternative."
    intro="If you're using PrisonEngine to receive prison calls on a local landline, Cellblock B offers the same core service — a local Australian landline forwarded to your mobile — at a flat, unbeatable subscription. $1 a day — billed $30 every 30 days. Unlimited calls. Lowest price in Australia, guaranteed."
    brand="PrisonEngine"
    bottomBlurb="Comparisons reference publicly-available plans and feature pages on prisonengine.com.au at time of writing. Cellblock B is independent and not affiliated with PrisonEngine. Pricing on either side may change."
    table={{
      competitorName: "PrisonEngine",
      items: [
        
        { label: "Headline price", cbb: "$1 a day ($30 / 30 days)", them: "Varies — check site" },
        
        { label: "Unlimited calls included", cbb: true, them: "Varies" },
        { label: "Lowest-price guarantee in Australia", cbb: true, them: false },
        { label: "10-day money-back guarantee", cbb: true, them: false },
        { label: "Local Australian landline number", cbb: true, them: true },
        { label: "Forwards to your existing mobile", cbb: true, them: true },
        { label: "No app required", cbb: true, them: true },
        { label: "Instant activation", cbb: true, them: "Varies" },
        { label: "Cancel anytime", cbb: true, them: true },
      ],
    }}
    faq={[
      ["How is Cellblock B different from PrisonEngine?", "Cellblock B is built around one principle: flat-rate unlimited calls for the lowest published price in Australia. $1 a day — billed $30 every 30 days, with a 10-day money-back guarantee."],
      ["Will I lose my number if I switch?", "Numbers are issued per provider and aren't portable between services. Cellblock B will issue you a fresh local landline in the relevant Australian state at signup, and email it to you within minutes."],
      ["Do you cover the same prisons as PrisonEngine?", "Cellblock B covers every Australian adult correctional centre — across NSW, VIC, QLD, WA, SA, TAS, ACT and NT. See our state pages for the full list."],
      ["What's your refund policy?", "Cellblock B offers a 10-day money-back guarantee on first-time subscriptions. You can also cancel anytime from your billing portal."],
    ]}
  />
);

// ============================================================================
// Page 4 — Cellblock B vs PrisonEngine
// ============================================================================
export const CellblockBVsPrisonEnginePage = () => (
  <Shell
    slug="cellblock-b-vs-prisonengine"
    title="Cellblock B vs PrisonEngine | Compare Prison Call Plans Australia"
    description="Cellblock B vs PrisonEngine — side-by-side comparison of prison call forwarding in Australia. Pricing, features, coverage and FAQs. Cellblock B from $1 a day ($30 every 30 days), unlimited calls."
    h1="Cellblock B vs PrisonEngine."
    intro="Both services hand you a local Australian landline number that an inmate dials as a local call, then forward that call to your mobile. The differences come down to price, included call volume, and money-back guarantee. Here's the side-by-side."
    brand="PrisonEngine"
    bottomBlurb="Comparison references publicly-listed plans on each provider's website at time of writing. Pricing on either side may change — verify current rates on the relevant homepage before deciding."
    table={{
      competitorName: "PrisonEngine",
      items: [
        
        { label: "Headline price", cbb: "$1 a day ($30 / 30 days) — flat", them: "Varies — check site" },
        
        { label: "Unlimited calls included", cbb: true, them: "Varies" },
        { label: "Lowest-price guarantee", cbb: true, them: false },
        { label: "10-day money-back guarantee", cbb: true, them: false },
        { label: "Local landline + mobile forwarding", cbb: true, them: true },
        { label: "All Australian states & territories", cbb: true, them: true },
        { label: "Cancel anytime", cbb: true, them: true },
      ],
    }}
    faq={[
      ["Why pick Cellblock B over PrisonEngine?", "Cellblock B publishes the lowest flat-rate price in Australia ($1 a day ($30 every 30 days)) and backs it with a 10-day money-back guarantee. Both services hand you a local landline that forwards to your mobile, but Cellblock B's flat-unlimited model removes any risk of usage spikes."],
      ["Can I switch in one day?", "Yes. Numbers are provisioned automatically as soon as your payment is confirmed and emailed to you within minutes."],
      ["What if my loved one is transferred between prisons?", "Cellblock B will provide a replacement local number in the new state where available, at no extra cost."],
      ["Can I cancel anytime?", "Yes. Cancel any time from your billing portal — subscriptions will not auto-renew after cancellation."],
    ]}
  />
);

// ============================================================================
// Page 5 — Cheapest prison call service in Australia (anchor page)
// ============================================================================
export const CheapestPrisonCallPage = () => (
  <Shell
    slug="cheapest-prison-call-service-australia"
    title="Cheapest Prison Call Service in Australia | Cellblock B — $1 a day"
    description="Looking for the cheapest prison call service in Australia? Cellblock B is $1 a day ($30 every 30 days) — flat-rate, unlimited prison calls forwarded to your mobile."
    h1="The cheapest prison call service in Australia."
    intro="Cellblock B publishes the lowest flat-rate price for prison-call forwarding in Australia. $1 a day — billed $30 every 30 days for unlimited inbound calls from any Australian correctional centre, bridged to the mobile you already carry. If you find a comparable like-for-like Australian service cheaper, tell us and we'll honour it."
    brand="other Australian prison-call providers"
    bottomBlurb="Comparisons reference publicly-listed pricing on each provider's website at time of writing. Pricing on either side may change — always verify current rates before deciding."
    table={{
      competitorName: "Typical AU competitor",
      items: [
        { label: "Headline plan", cbb: "$1 a day ($30 every 30 days)", them: "$5–$10/week + usage" },
        { label: "Unlimited calls included", cbb: true, them: false },
        { label: "One flat price", cbb: "$1 a day", them: "Rare" },
        { label: "Lowest-price guarantee in Australia", cbb: true, them: false },
        { label: "10-day money-back guarantee", cbb: true, them: "Varies" },
        { label: "Local landline + mobile forwarding", cbb: true, them: true },
        { label: "All Australian states & territories", cbb: true, them: "Varies" },
        { label: "Instant activation", cbb: true, them: "Varies" },
        { label: "No app, no new SIM", cbb: true, them: true },
        { label: "Cancel anytime", cbb: true, them: "Varies" },
      ],
    }}
    faq={[
      ["Is Cellblock B really the cheapest in Australia?", "Yes — we publish the lowest flat-rate price for prison-call forwarding in Australia ($1 a day ($30 every 30 days)). If you find a comparable like-for-like Australian service at a lower flat rate, contact us and we'll honour it."],
      ["What's included for $30 / 30 days?", "Unlimited inbound prison calls from any Australian correctional centre, forwarded to your mobile. No per-minute charges, no setup fees, no app to install."],
      ["What if I only need it for a few weeks?", "Choose the monthly plan — you can cancel any time from your billing portal. If you cancel within 10 days, we offer a full money-back guarantee."],
      ["Are there cheaper providers if I pay weekly?", "Some providers charge a low weekly base fee but add per-minute call costs on top. For most families, Cellblock B's flat unlimited model works out cheaper once call volume is included. We encourage you to verify each provider's current pricing before deciding."],
      ["Does Cellblock B cover every Australian prison?", "Yes. We service every adult correctional centre in NSW, VIC, QLD, WA, SA, TAS, ACT and NT — see our state pages for the full directory."],
    ]}
  />
);

export const COMPETITOR_ROUTES = [
  { path: "/prisonconnect-alternative", element: <PrisonConnectAlternativePage /> },
  { path: "/cellblock-b-vs-prisonconnect", element: <CellblockBVsPrisonConnectPage /> },
  { path: "/prisonengine-alternative", element: <PrisonEngineAlternativePage /> },
  { path: "/cellblock-b-vs-prisonengine", element: <CellblockBVsPrisonEnginePage /> },
  { path: "/cheapest-prison-call-service-australia", element: <CheapestPrisonCallPage /> },
];
